import { useSubscription } from '@apollo/client';
import { useRouter } from 'next/router';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { Reducer, useImmerReducer } from 'use-immer';
import StoryblokClient, { StoryblokResult } from 'storyblok-js-client';
import Auth from '@aws-amplify/auth';
import { SETTINGS_SUBSCRIPTION } from '../graphql/subscriptions';
import Storyblok from '../libs/storyblok';
import { getEventRoot, getSlug } from '../libs/utils';
import { AuthContext } from './authContext';

export type Settings = {
  name: string;
  slug: string;
  bgColor?: string;
  bgImg?: string;
  navbar?: any;
  bannerImg?: string;
  bgRepeat?: string;
  darkMode: boolean;
  headingColor?: string;
  loginRequired: boolean;
  loginType: string;
  navbarBgColor?: string;
  navbarImg?: string;
  defaultMargin?: string;
  hasBooths?: boolean;
  hasBooth?: boolean;
  hasBreakouts?: boolean;
  hasBreakout?: boolean;
  hasCertificate?: boolean;
  hasRewards?: boolean;
  hasReward?: boolean;
  styles?: string;
  hiddenIds?: string[];

  signoutRedirect?: any;

  domainWhiteList?: string[];
};

export type EventStateContextType = Settings & {
  isLoading: boolean;
  storyblok: any;
  viewedVideos?: string[];
};

export type EventDispatchContextType = {
  setSettings: (settings: Settings) => void;
  setHiddenIds: (ids: string[]) => void;
  storyblokGet: (slug: string, params?: any) => Promise<StoryblokResult>;
  setVideoViewed: (video: any) => void;
};

type EventContextProps = {
  children: React.ReactNode;
};

export const EventStateContext = createContext({} as EventStateContextType);
export const EventDispatchContext = createContext({} as EventDispatchContextType);

const eventReducer: Reducer<EventStateContextType, any> = (draft, action) => {
  switch (action.type) {
    case 'setSettings': {
      draft.isLoading = false;
      draft.name = action.settings.name;
      draft.slug = action.settings.slug;
      draft.bgColor = action.settings.bgColor || undefined;
      draft.bgImg = action.settings.bgImg || undefined;
      draft.navbar = action.settings.navbar || undefined;
      draft.bannerImg = action.settings.bannerImg || undefined;
      draft.bgRepeat = action.settings.bgRepeat || undefined;
      draft.darkMode = action.settings.darkMode || false;
      draft.headingColor = action.settings.headingColor || undefined;
      draft.loginRequired = action.settings.loginRequired || false;
      draft.loginType = action.settings.loginType;
      draft.navbarBgColor = action.settings.navbarBgColor || false;
      draft.navbarImg = action.settings.navbarImg || false;
      draft.defaultMargin = action.settings.defaultMargin || '6';
      draft.hasBooths = action.settings.hasBooths || false;
      draft.hasBooth = action.settings.hasBooth || false;
      draft.hasBreakouts = action.settings.hasBreakouts || false;
      draft.hasBreakout = action.settings.hasBreakout || false;
      draft.hasCertificate = action.settings.hasCertificate || false;
      draft.hasRewards = action.settings.hasRewards || false;
      draft.hasReward = action.settings.hasReward || false;
      draft.styles = action.settings.styles || '';
      draft.hiddenIds = action.settings.hiddenIds;

      draft.signoutRedirect = action.settings.signoutRedirect || undefined;

      draft.domainWhiteList = action.settings.domainWhiteList || undefined;
      break;
    }
    case 'setHiddenIds': {
      draft.hiddenIds = action.ids;
      break;
    }
    case 'setVideoViewed': {
      draft.viewedVideos = action.videos;
      break;
    }
    default: {
      throw new Error(`Unhandled dispatch: ${action.type}`);
    }
  }
};

const initialState = {
  isLoading: true,
  darkMode: false,
  hiddenIds: []
};

let storyblok = Storyblok;
export function EventProvider({ children }: EventContextProps): any {
  const [state, dispatch] = useImmerReducer<any, any>(eventReducer, initialState);
  const { authenticating, hasCognitoSession } = useContext(AuthContext);
  const router = useRouter();
  const { slug, viewedVideos } = state;

  const { data: hiddenData, loading: hiddenLoading, error: hiddenError } = useSubscription(SETTINGS_SUBSCRIPTION, {
    variables: {
      eventSlug: getSlug(router.asPath)
    }
  });

  const setStoryblokClient = () => {
    // Use restricted for signed-in users
    if (!authenticating && hasCognitoSession) {
      storyblok = new StoryblokClient(
        {
          accessToken: ':token',
          cache: {
            clear: 'auto',
            type: 'memory'
          }
        },
        process.env.STORYBLOK_RESTRICTED_PROXY
      );
    } else {
      storyblok = Storyblok;
    }
  };

  const storyblokGet = async (storyblokSlug: string, params?: any): Promise<StoryblokResult> => {
    // Use the latest auth token
    if (!authenticating && hasCognitoSession) {
      storyblok.client.defaults.headers.common.Authorization = (await Auth.currentSession()).getIdToken().getJwtToken();
    }

    return storyblok.get(storyblokSlug, params);
  };

  const setSettings = (newSettings: Settings) => {
    dispatch({ type: 'setSettings', settings: newSettings });
  };

  const setHiddenIds = (ids) => {
    dispatch({ type: 'setHiddenIds', ids });
  };

  const setVideoViewed = (video: string) => {
    if (viewedVideos) {
      if (!viewedVideos.find((vid: string) => vid === video))
        dispatch({ type: 'setVideoViewed', videos: [...viewedVideos, video] });
    } else {
      dispatch({ type: 'setVideoViewed', videos: [video] });
    }
  };

  useEffect(() => {
    const path: any = getEventRoot(router.asPath);

    // Only load new settings for a new event
    if (getEventRoot(slug) === path) {
      return;
    }

    const storyParams: any = {
      starts_with: `${path}/conference-settings`
    };

    // TODO: allow preview instead of published
    if (router.query /* || this.devMode */ || (typeof window !== 'undefined' && window.storyblok)) {
      storyParams.version = 'draft';
    }

    // Always use public path for loading settings
    Storyblok.get('cdn/stories', storyParams)
      .then(({ data }: any) => {
        // console.log('Storyblok.get then', data);

        if (data.stories.length < 1) {
          // console.log('Event not set');
          return;
        }
        const {
          name,
          backgroundColor,
          backgroundImageRepeat,
          bannerImage,
          backgroundImage,
          darkMode,
          headingColor,
          loginRequired,
          navbar,
          loginType,
          navbarBackgroundColor,
          navbarImage,
          defaultMargin,
          hasBooths,
          hasBooth,
          hasBreakouts,
          hasBreakout,
          hasCertificate,
          hasRewards,
          hasReward,
          styles,
          hiddenIds,
          signoutRedirect,
          domainWhiteList
        } = data.stories[0]?.content;

        // Values set for return initial Ids of Compontents

        if (name.match(/^<route>.*<\/route>$/)) {
          router.push(name.split('<route>')[1].split('</route>')[0]).catch((e) => {
            console.error(`Unable to route to ${name.split('<route>')[1].split('</route>')[0]}`);
          });
          return;
        }

        const newSettings: Settings = {
          name,
          headingColor,
          darkMode,
          defaultMargin,
          hasBooths,
          hasBooth,
          hasBreakout,
          hasBreakouts,
          hasCertificate,
          hasRewards,
          hasReward,
          bgColor: backgroundColor,
          bgRepeat: backgroundImageRepeat,
          bannerImg: bannerImage?.filename,
          bgImg: backgroundImage?.filename,
          slug: `${path}/`,
          loginRequired,
          loginType,
          navbarBgColor: navbarBackgroundColor,
          navbar,
          navbarImg: navbarImage?.filename,
          styles,
          hiddenIds,
          signoutRedirect,
          domainWhiteList: domainWhiteList?.split(/\r?\n/) || []
        };

        setSettings(newSettings);
      })
      .catch((err) => {
        console.error('storyblok.get error', err, { name: err.name, message: err.message });
      });
  }, [router]);

  // Use restricted storyblok paths for cognito sessions
  useEffect(() => {
    setStoryblokClient();
  }, [hasCognitoSession]);

  // hiddenIds
  useEffect(() => {
    if (hiddenData && hiddenData.updateSettings) {
      setHiddenIds(hiddenData.updateSettings.ids);
    }
  }, [hiddenData]);

  const dispatchContextValue = useMemo(() => ({ setSettings, setHiddenIds, storyblokGet, setVideoViewed }), [
    setSettings,
    setHiddenIds,
    storyblokGet,
    setVideoViewed
  ]);
  return (
    <EventDispatchContext.Provider value={dispatchContextValue}>
      <EventStateContext.Provider value={state}>{children}</EventStateContext.Provider>
    </EventDispatchContext.Provider>
  );
}
